import { zeroErrorMessage } from "@/lib/zero-error";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import { toast } from "@/hooks/use-toast";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { generateUUID } from "@/lib/utils";
import {
  reportServerMutationFailure,
  toLegacyTimestamp,
  usePendingMutation,
  type MutationOptions,
  type ZeroMutateClient,
} from "./useZeroMutationUtils";

export interface StudioDocument {
  id: string;
  title: string;
  studio_id: string;
  document_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

type ZeroStudioDocument = {
  id: string;
  title: string;
  studioID: string;
  documentID?: string | null;
  createdBy?: string | null;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
};

function toLegacyDocument(row: ZeroStudioDocument): StudioDocument {
  return {
    id: row.id,
    title: row.title,
    studio_id: row.studioID,
    document_id: row.documentID ?? null,
    created_by: row.createdBy ?? null,
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
  };
}

export function useStudioDocuments(studioId: string | undefined) {
  const query = studioId
    ? appQueries.studioDocuments.byStudio({ studioID: studioId })
    : undefined;
  const [data, details] = useQuery(query, { enabled: !!studioId });
  const docs = ((data ?? []) as ZeroStudioDocument[]).map(toLegacyDocument);
  return {
    data: docs,
    isLoading: !!studioId && details.type !== "complete" && docs.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useCreateStudioDocument() {
  const zero = useZero() as ZeroMutateClient;
  const { isOffline } = useOnlineStatus();
  const { isPending, runPending } = usePendingMutation();
  const run = (input: { title: string; studio_id: string }) =>
    runPending(async () => {
      if (isOffline) {
        throw new Error("Documents can be created when you are online.");
      }

      const mutationInput = {
        id: generateUUID(),
        documentID: generateUUID(),
        title: input.title,
        studioID: input.studio_id,
      };
      const mutation = zero.mutate(
        appMutators.studioDocuments.create(mutationInput),
      );
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Studio document create was rejected",
      );
      return toLegacyDocument(mutationInput);
    });
  return {
    mutate: (
      input: { title: string; studio_id: string },
      options?: MutationOptions<StudioDocument>,
    ) => {
      void run(input)
        .then((data) => {
          toast({
            title: "Document created",
            description:
              "The documentation page has been created successfully.",
          });
          options?.onSuccess?.(data);
        })
        .catch((error) =>
          toast({
            title: "Error creating document",
            description: error.message,
            variant: "destructive",
          }),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useUpdateStudioDocument() {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();
  const run = ({ id, title }: { id: string; title: string }) =>
    runPending(async () => {
      const mutation = zero.mutate(
        appMutators.studioDocuments.update({ id, title }),
      );
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Studio document update was rejected",
      );
      return { id };
    });
  return {
    mutate: (
      input: { id: string; title: string },
      options?: MutationOptions<{ id: string }>,
    ) => {
      void run(input)
        .then((data) => {
          toast({
            title: "Document updated",
            description:
              "The documentation page has been updated successfully.",
          });
          options?.onSuccess?.(data);
        })
        .catch((error) =>
          toast({
            title: "Error updating document",
            description: error.message,
            variant: "destructive",
          }),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useDeleteStudioDocument() {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();
  const run = (id: string) =>
    runPending(async () => {
      const mutation = zero.mutate(appMutators.studioDocuments.delete({ id }));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Studio document delete was rejected",
      );
      return { id };
    });
  return {
    mutate: (id: string, options?: MutationOptions<{ id: string }>) => {
      void run(id)
        .then((data) => {
          toast({
            title: "Document deleted",
            description:
              "The documentation page has been deleted successfully.",
          });
          options?.onSuccess?.(data);
        })
        .catch((error) =>
          toast({
            title: "Error deleting document",
            description: error.message,
            variant: "destructive",
          }),
        );
    },
    mutateAsync: run,
    isPending,
  };
}
